import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, toConsumableArray as _toConsumableArray } from './_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useRef, useEffect, useContext } from 'react';
import { FormItem, FormLabel, Icon, FormInputChoice } from '@tesla/design-system-react';
import { Option } from './Option.js';
import { OptionGroup } from './OptionGroup.js';
import { OptionGroupContext } from './context.js';
import { useFieldApi, useFieldState } from 'informed';
import { FeedBack } from './Feedback.js';
import { iconChevron } from '@tesla/design-system-icons';

var _excluded = ["label", "includes", "groupLabel", "showSelectedCount", "showLabelsThenCount", "showSelectedButton", "showSelectedLabels", "optionsClassname", "containerClassname", "optionsOpenClassname", "selectedOptionsClassName", "unSelectedOptionsClassName", "optionGroupClassname", "resetOnSearch", "options", "maxHight", "maxHeight", "noScroll", "showSelectedOptions", "showOnlySelected", "selectAll", "placeholder", "dropdownPlaceholder", "noSearch", "dropDown", "id", "noCase", "onSearchChange"];

/**
 * @see https://usehooks.com/useDebounce/
 */
function useDebounce(value, delay) {
  // State and setters for debounced value
  var _useState = useState(value),
    _useState2 = _slicedToArray(_useState, 2),
    debouncedValue = _useState2[0],
    setDebouncedValue = _useState2[1];
  useEffect(function () {
    // Update debounced value after delay
    var handler = setTimeout(function () {
      setDebouncedValue(value);
    }, delay);

    // Cancel the timeout if value changes (also on delay change or unmount)
    // This is how we prevent debounced value from updating if value is changed ...
    // .. within the delay period. Timeout gets cleared and restarted.
    return function () {
      clearTimeout(handler);
    };
  }, [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

/**
 * Hook that alerts clicks outside of the passed ref
 * @see https://stackoverflow.com/questions/32553158/detect-click-outside-react-component
 */
function useOutsideAlerter(ref, action) {
  useEffect(function () {
    /**
     * Alert if clicked on outside of element
     *
     * @param {Event} event
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        action();
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return function () {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);
}

/** @param {Object.<string, *>} props */
var MultiSelectionToggle = function MultiSelectionToggle(_ref) {
  var wrapperStyle = _ref.wrapperStyle,
    checkboxId = _ref.checkboxId,
    checked = _ref.checked,
    onChange = _ref.onChange,
    label = _ref.label;
    _ref.selectAll;
    var groupLabel = _ref.groupLabel;
  // console.log('CHECKED', checked);
  if (groupLabel) {
    return /*#__PURE__*/React.createElement("div", {
      style: wrapperStyle
    }, label);
  }
  return /*#__PURE__*/React.createElement("div", {
    style: wrapperStyle
  }, /*#__PURE__*/React.createElement(FormInputChoice, {
    id: checkboxId,
    label: label,
    value: checked,
    checked: checked,
    onChange: onChange
  }));
};

/** @param {Object.<string, *>} props */
var DropdownInput = function DropdownInput(_ref2) {
  var openDropdown = _ref2.openDropdown,
    closeDropdown = _ref2.closeDropdown,
    open = _ref2.open,
    nItems = _ref2.nItems,
    sItems = _ref2.sItems,
    sItmesAndCount = _ref2.sItmesAndCount,
    label = _ref2.label,
    id = _ref2.id,
    error = _ref2.error,
    errorLabel = _ref2.errorLabel,
    disabled = _ref2.disabled,
    name = _ref2.name,
    dropdownPlaceholder = _ref2.dropdownPlaceholder;
  var _useFieldState = useFieldState(name),
    selected = _useFieldState.value;
  /** @type {React.MutableRefObject<?HTMLInputElement>} */
  var selectInputRef = useRef(null);
  var handleClick = function handleClick() {
    if (open) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };
  var selectedValues = selected && selected.length > 0 ? "".concat(nItems, " ").concat(sItems, " ").concat(sItmesAndCount) : '';
  useEffect(function () {
    /**
     * Handling dynamic height of textarea for the size of selected labels. By default it looks as same as input text box.
     * Reference: https://stackoverflow.com/questions/39401504/javascript-react-dynamic-height-textarea-stop-at-a-max
     * */
    if (selectInputRef.current) {
      selectInputRef.current.style.height = '40px'; //Current height of input text box
      if (selectInputRef.current.scrollHeight && selectInputRef.current.scrollHeight > 40) {
        selectInputRef.current.style.height = "".concat(selectInputRef.current.scrollHeight, "px");
      }
    }
  }, [selectedValues]);
  return /*#__PURE__*/React.createElement(FormItem, {
    error: error,
    variant: "text"
  }, label ? /*#__PURE__*/React.createElement(FormLabel, {
    htmlFor: id
  }, label) : null, /*#__PURE__*/React.createElement("div", {
    className: "tds-form-input tds-form-input--default select-search-dropdown_input"
  }, /*#__PURE__*/React.createElement("input", {
    className: "tds-form-input-text",
    ref: selectInputRef,
    style: {
      resize: 'none',
      overflow: 'hidden'
    },
    onClick: handleClick,
    role: "listbox",
    autoComplete: "off",
    disabled: disabled,
    placeholder: dropdownPlaceholder || "Select ".concat(label),
    "aria-expanded": !!open,
    value: selectedValues,
    readOnly: true
  }), /*#__PURE__*/React.createElement("div", {
    className: "tds-form-input-trailing",
    onClick: handleClick
  }, /*#__PURE__*/React.createElement(Icon, {
    inline: true,
    data: iconChevron
  }))), /*#__PURE__*/React.createElement(FeedBack, {
    error: errorLabel,
    showError: error
  }));
};

/**
 * @typedef {Object} OptionGroupProps
 * @property {Array<{ disabled: Boolean, value: *, label: String, group: String }>} options
 *
 * @param {OptionGroupProps & Object.<string, *>} props
 */
var OptionGroups = function OptionGroups(_ref3) {
  var options = _ref3.options,
    id = _ref3.id,
    setValue = _ref3.setValue,
    getValue = _ref3.getValue,
    showOnlySelected = _ref3.showOnlySelected,
    selectAll = _ref3.selectAll,
    groupLabel = _ref3.groupLabel;
  /** @type {?Object} */
  var currentGroup = null;
  var optionGroups = [];

  // Get the current value
  var value = getValue() || [];

  // Get Select All Feature
  if (selectAll && !showOnlySelected && options.length > 0) {
    var values = options.filter(function (o) {
      return !o.disabled;
    }).map(function (o) {
      return o.value;
    });
    var checked = values.length === value.length || values.every( /** @param {Array<*>} v */
    function (v) {
      return value.includes(v);
    });
    optionGroups.push( /*#__PURE__*/React.createElement(MultiSelectionToggle, {
      key: "".concat(id, "-").concat(selectAll, "-selectAllComponent"),
      checkboxId: "".concat(id, "-selectAll"),
      groupLabel: groupLabel,
      label: selectAll,
      selectAll: true,
      checked: checked,
      wrapperStyle: {
        marginTop: '1rem',
        textAlign: 'left',
        marginBottom: options[0] && options[0].group ? '0px' : '1rem'
      },
      onChange: /** @param {React.SyntheticEvent<HTMLInputElement>} e */
      function onChange(e) {
        if (e.currentTarget.checked) {
          // filter out all options with that value
          var newValue = value.filter( /** @param {Array.<*>} v */
          function (v) {
            return !values.includes(v);
          });
          // Add all options to be selected
          setValue([].concat(_toConsumableArray(newValue), _toConsumableArray(values)));
        } else {
          // filter out all options with that value
          var _newValue = value.filter( /** @param {Array.<*>} v */
          function (v) {
            return !values.includes(v);
          });
          // Set empty array value
          setValue(_newValue);
        }
      }
    }));
  }
  optionGroups = optionGroups.concat(options.map(function (option) {
    // We found a new group
    if (option.group && option.group != currentGroup) {
      currentGroup = option.group;
      if (!showOnlySelected) {
        // Get all options of that value
        var valuesOfGroup = options.filter(function (o) {
          return o.group === option.group && !o.disabled;
        }).map(function (o) {
          return o.value;
        });
        var _checked = valuesOfGroup.every(function (v) {
          return value.includes(v);
        });
        return /*#__PURE__*/React.createElement(React.Fragment, {
          key: "".concat(id, "-").concat(option.group, "-").concat(option.value, "-wrapper")
        }, /*#__PURE__*/React.createElement("div", {
          style: {
            width: '100%'
          }
        }, /*#__PURE__*/React.createElement(MultiSelectionToggle, {
          groupLabel: groupLabel,
          key: "".concat(id, "-").concat(option.group, "Component-").concat(option.value),
          checkboxId: "".concat(id, "-").concat(option.group),
          label: option.group,
          checked: _checked,
          wrapperStyle: {
            marginTop: '2rem',
            textAlign: 'left',
            marginLeft: selectAll ? '2rem' : '0px'
          },
          onChange: /** @param {React.SyntheticEvent<HTMLInputElement>} e */
          function onChange(e) {
            if (e.currentTarget.checked) {
              // filter out all options with that value
              var newValue = value.filter( /** @param {Array.<*>} v */
              function (v) {
                return !valuesOfGroup.includes(v);
              });
              // Add those options to the currently selected
              setValue([].concat(_toConsumableArray(newValue), _toConsumableArray(valuesOfGroup)));
            } else {
              // filter out all options with that value
              var _newValue2 = value.filter( /** @param {Array.<*>} v */
              function (v) {
                return !valuesOfGroup.includes(v);
              });
              // Set the value to the new value
              setValue(_newValue2);
            }
          }
        })), /*#__PURE__*/React.createElement("div", {
          className: "tds-option",
          style: {
            marginLeft: selectAll ? '3rem' : '0px'
          }
        }, /*#__PURE__*/React.createElement(Option, {
          key: "".concat(id, "-").concat(option.group, "-").concat(option.value),
          value: option.value,
          label: option.label,
          disabled: option.disabled
        })));
      } else {
        return /*#__PURE__*/React.createElement("div", {
          className: "tds-option",
          style: {
            marginTop: '2rem',
            textAlign: 'left'
          },
          key: "".concat(id, "-").concat(option.group, "-").concat(option.value, "Wrapper")
        }, /*#__PURE__*/React.createElement("span", {
          className: "tds-option-group-title"
        }, option.group), /*#__PURE__*/React.createElement(Option, {
          key: "".concat(id, "-").concat(option.value),
          value: option.value,
          label: option.label,
          disabled: option.disabled
        }));
      }
    }

    // No new group found keep pushing
    return /*#__PURE__*/React.createElement("div", {
      className: "tds-option",
      style: {
        marginLeft: selectAll && option.group && !showOnlySelected ? '3rem' : '0px'
      },
      key: "".concat(id, "-").concat(option.group, "-").concat(option.value, "-wrapper")
    }, /*#__PURE__*/React.createElement(Option, {
      key: "".concat(id, "-").concat(option.value),
      value: option.value,
      label: option.label,
      disabled: option.disabled
    }));
  }));
  return /*#__PURE__*/React.createElement(React.Fragment, null, optionGroups);
};

/**
 * @typedef {Object} OptionsProps
 * @property {Array<*>} filteredOptions
 * @property {Array<*>} options
 *
 * @param {OptionsProps & Object.<string, *>} props
 */
var Options = function Options(_ref4) {
  var showSelectedOptions = _ref4.showSelectedOptions,
    options = _ref4.options,
    groupLabel = _ref4.groupLabel,
    filteredOptions = _ref4.filteredOptions,
    name = _ref4.name,
    selectedOptionsClassName = _ref4.selectedOptionsClassName,
    unSelectedOptionsClassName = _ref4.unSelectedOptionsClassName,
    optionGroupClassname = _ref4.optionGroupClassname,
    showOnlySelected = _ref4.showOnlySelected,
    selectAll = _ref4.selectAll,
    setValue = _ref4.setValue,
    getValue = _ref4.getValue;
  var _useContext = /** @type {OptionGroupContext} */
    useContext(OptionGroupContext),
    optionGroupContext = _useContext.optionGroupContext;
  var id = optionGroupContext.id;
  var _useFieldState2 = useFieldState(name),
    selected = _useFieldState2.value;
  if (showSelectedOptions || showOnlySelected) {
    var unSelectedOptions = selected ? filteredOptions.filter(function (option) {
      return !(Array.isArray(selected) ? selected.includes(option.value) : selected === option.value);
    }) : filteredOptions;
    var selectedOptions = selected ? options.filter(function (option) {
      return Array.isArray(selected) ? selected.includes(option.value) : selected === option.value;
    }) : [];
    return /*#__PURE__*/React.createElement("div", {
      style: {
        width: '100%'
      },
      className: optionGroupClassname || ''
    }, selected && selected.length > 0 ? /*#__PURE__*/React.createElement("div", {
      style: {
        width: '100%',
        marginBottom: '1rem'
      },
      className: selectedOptionsClassName || ''
    }, /*#__PURE__*/React.createElement(OptionGroups, {
      groupLabel: groupLabel,
      options: selectedOptions,
      id: id,
      setValue: setValue,
      getValue: getValue,
      showOnlySelected: showOnlySelected,
      selectAll: selectAll
    })) : null, !showOnlySelected ? /*#__PURE__*/React.createElement("div", {
      style: {
        width: '100%'
      },
      className: unSelectedOptionsClassName || ''
    }, /*#__PURE__*/React.createElement(OptionGroups, {
      groupLabel: groupLabel,
      options: unSelectedOptions,
      id: id,
      setValue: setValue,
      getValue: getValue,
      showOnlySelected: showOnlySelected,
      selectAll: selectAll
    })) : null);
  }
  return /*#__PURE__*/React.createElement(OptionGroups, {
    groupLabel: groupLabel,
    options: filteredOptions,
    id: id,
    setValue: setValue,
    getValue: getValue,
    showOnlySelected: showOnlySelected,
    selectAll: selectAll
  });
};
var SelectSearch = function SelectSearch(_ref5) {
  var _rest$name, _rest$name2;
  var label = _ref5.label,
    includes = _ref5.includes,
    groupLabel = _ref5.groupLabel,
    showSelectedCount = _ref5.showSelectedCount,
    showLabelsThenCount = _ref5.showLabelsThenCount;
    _ref5.showSelectedButton;
    var showSelectedLabels = _ref5.showSelectedLabels,
    optionsClassname = _ref5.optionsClassname,
    containerClassname = _ref5.containerClassname,
    optionsOpenClassname = _ref5.optionsOpenClassname,
    selectedOptionsClassName = _ref5.selectedOptionsClassName,
    unSelectedOptionsClassName = _ref5.unSelectedOptionsClassName,
    optionGroupClassname = _ref5.optionGroupClassname,
    resetOnSearch = _ref5.resetOnSearch,
    _ref5$options = _ref5.options,
    options = _ref5$options === void 0 ? [] : _ref5$options,
    maxHight = _ref5.maxHight,
    maxHeight = _ref5.maxHeight,
    noScroll = _ref5.noScroll,
    showSelectedOptions = _ref5.showSelectedOptions,
    showOnlySelected = _ref5.showOnlySelected,
    selectAll = _ref5.selectAll,
    placeholder = _ref5.placeholder,
    dropdownPlaceholder = _ref5.dropdownPlaceholder,
    noSearch = _ref5.noSearch,
    dropDown = _ref5.dropDown,
    id = _ref5.id,
    noCase = _ref5.noCase,
    onSearchChange = _ref5.onSearchChange,
    rest = _objectWithoutProperties(_ref5, _excluded);
  // TODO: can we make this a more traditional input?
  var _useFieldApi = useFieldApi((_rest$name = rest.name) !== null && _rest$name !== void 0 ? _rest$name : ''),
    reset = _useFieldApi.reset,
    setFieldValue = _useFieldApi.setValue,
    getValue = _useFieldApi.getValue;
  var _useFieldState3 = useFieldState((_rest$name2 = rest.name) !== null && _rest$name2 !== void 0 ? _rest$name2 : ''),
    selected = _useFieldState3.value,
    error = _useFieldState3.error,
    showError = _useFieldState3.showError;
  var disabled = rest.disabled;

  // The value of search box
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    value = _useState4[0],
    setValue = _useState4[1];

  // To force the filter
  var _useState5 = useState({}),
    _useState6 = _slicedToArray(_useState5, 2),
    forceFilter = _useState6[0],
    setForceFilter = _useState6[1];

  // The filtered options
  var _useState7 = useState(options),
    _useState8 = _slicedToArray(_useState7, 2),
    filteredOptions = _useState8[0],
    setFilteredOptions = _useState8[1];

  // The devounced value
  var debouncedValue = useDebounce(value, 250);

  // View selected
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    viewSelected = _useState10[0],
    setViewSelected = _useState10[1];

  // For dropdown
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    open = _useState12[0],
    setOpen = _useState12[1];
  var closeDropdown = function closeDropdown() {
    setOpen(false);
  };
  var openDropdown = function openDropdown() {
    setOpen(true);
  };
  var ref = useRef(null);
  useOutsideAlerter(ref, closeDropdown);

  // Effect for search
  useEffect(function () {
    if (viewSelected) {
      setFilteredOptions(options.filter(function (option) {
        return selected && selected.includes(option.value);
      }));
    } else if (debouncedValue) {
      setViewSelected(false);
      if (resetOnSearch) {
        reset();
      }
      if (includes) {
        setFilteredOptions(options.filter(function (option) {
          var searchCritiria = debouncedValue.split(',').map(function (r) {
            return noCase ? r.trim().toLowerCase() : r.trim();
          }).filter(function (r) {
            return r !== '';
          });
          return searchCritiria.some(function (r) {
            var aa = r.split('+').map(function (s) {
              return noCase ? s.trim().toLowerCase() : s.trim();
            }).filter(function (s) {
              return s !== '';
            });
            return aa.every(function (s) {
              return noCase ? option.label.toLowerCase().includes(s) : option.label.includes(s);
            });
          });
        }));
      } else {
        setFilteredOptions(options.filter(function (option) {
          var searchCritiria = debouncedValue.split(',').map(function (r) {
            return noCase ? r.trim().toLowerCase() : r.trim();
          }).filter(function (r) {
            return r !== '';
          });
          return searchCritiria.some(function (r) {
            var aa = r.split('+').map(function (s) {
              return noCase ? s.trim().toLowerCase() : s.trim();
            }).filter(function (s) {
              return s !== '';
            });
            return aa.every(function (s) {
              return noCase ? option.label.toLowerCase().startsWith(s) : option.label.startsWith(s);
            });
          });
        }));
      }
    } else {
      setFilteredOptions(options);
    }
  }, [debouncedValue, options, viewSelected, forceFilter] // Only call effect if debounced search term changes
  );
  var onReset = function onReset() {
    setValue('');
    setForceFilter({});
  };

  /**
   * Set the value on input change
   *
   * @param {React.SyntheticEvent<HTMLInputElement>} evt
   */
  var onChange = function onChange(evt) {
    setValue(evt.currentTarget.value);
    if (onSearchChange) onSearchChange(evt.currentTarget.value);
    evt.currentTarget.style.height = '40px'; //Current height of input text box
    evt.currentTarget.style.height = "".concat(evt.currentTarget.scrollHeight, "px");
  };
  var nItems = showSelectedCount ? "".concat(selected ? selected.length : 0, "/").concat(options.length, " items selected") : '';
  var selectedOptions = options.filter(function (option) {
    return selected && Array.isArray(selected) ? selected.includes(option.value) : selected === option.value;
  });
  var selectedLabels = selectedOptions.map(function (opt) {
    return opt.label;
  });
  var sItems = showSelectedLabels && selected && selected.length > 0 ? "".concat(selectedLabels.join(' - ')) : '';
  var sItmesAndCount = showLabelsThenCount && selected && selected.length > 0 ? "".concat(selectedLabels.slice(0, +showLabelsThenCount).join(' - ')).concat(selectedLabels.length > showLabelsThenCount ? " + ".concat(selectedLabels.length - showLabelsThenCount) : '') : '';

  /** @type {React.CSSProperties} */
  var style = {
    overflowY: noScroll ? 'visible' : 'scroll',
    marginBottom: '1rem',
    width: '100%'
  };
  if (!noScroll) {
    style.maxHeight = maxHight || maxHeight || '300px';
  }

  // Return input and option group
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: containerClassname
  }, !noSearch && !dropDown ? /*#__PURE__*/React.createElement(FormItem, {
    error: showError,
    variant: "text"
  }, label ? /*#__PURE__*/React.createElement(FormLabel, {
    htmlFor: id
  }, "".concat(label, " ").concat(nItems, " ").concat(sItems, " ").concat(sItmesAndCount)) : null, /*#__PURE__*/React.createElement("div", {
    className: "tds-form-input tds-form-input--default"
  }, /*#__PURE__*/React.createElement("input", {
    onChange: onChange,
    value: value || '',
    className: "tds-form-input-text",
    disabled: disabled,
    style: {
      resize: 'none',
      minHeight: '30px',
      overflow: 'hidden'
    },
    onFocus: openDropdown,
    placeholder: placeholder || 'Search'
  })), /*#__PURE__*/React.createElement(FeedBack, {
    error: error,
    showError: showError
  })) : null, dropDown ? /*#__PURE__*/React.createElement(DropdownInput, {
    label: label,
    disabled: disabled,
    openDropdown: openDropdown,
    closeDropdown: closeDropdown,
    open: open,
    error: showError,
    errorLabel: error,
    name: rest.name,
    nItems: nItems,
    sItems: sItems,
    sItmesAndCount: sItmesAndCount,
    dropdownPlaceholder: dropdownPlaceholder
  }) : null, /*#__PURE__*/React.createElement("div", {
    style: style,
    className: "".concat(optionsClassname || '', " ").concat(open ? optionsOpenClassname : '')
  }, dropDown && !noSearch ? /*#__PURE__*/React.createElement(FormItem, null, /*#__PURE__*/React.createElement("div", {
    className: "tds-form-input tds-form-input--default"
  }, /*#__PURE__*/React.createElement("input", {
    onChange: onChange,
    value: value || '',
    className: "tds-form-input-text",
    disabled: disabled,
    style: {
      resize: 'none',
      minHeight: '30px',
      overflow: 'hidden'
    },
    placeholder: placeholder || 'Search'
  }))) : null, /*#__PURE__*/React.createElement(OptionGroup, _extends({}, rest, {
    onReset: onReset,
    noFormItem: true
  }), /*#__PURE__*/React.createElement(Options, {
    groupLabel: groupLabel,
    setValue: setFieldValue,
    getValue: getValue,
    optionGroupClassname: optionGroupClassname,
    unSelectedOptionsClassName: unSelectedOptionsClassName,
    selectedOptionsClassName: selectedOptionsClassName,
    showSelectedOptions: showSelectedOptions,
    showOnlySelected: showOnlySelected,
    selectAll: selectAll,
    options: options,
    filteredOptions: filteredOptions,
    name: rest.name
  }))));
};

export { SelectSearch };
